import React from 'react';
import { Box, Flex, HStack, Text, Icon, IconButton, keyframes } from '@chakra-ui/react';
import { FiTrash2, FiSend, FiChevronLeft } from 'react-icons/fi';
import { mmss } from './time';

/**
 * The recording bar that replaces the message box while a voice note is being
 * captured: a pulsing red dot, the clock, a live level meter, "slide to
 * cancel", and the trash and send buttons.
 *
 * On touch the whole bar tracks a leftward drag and cancels past a threshold,
 * the WhatsApp gesture; with a mouse the two buttons do the same job.
 */
const pulse = keyframes`
	0%, 100% { opacity: 1; transform: scale(1); }
	50% { opacity: 0.4; transform: scale(0.8); }
`;

const CANCEL_PX = 80;
const BARS = 14;

export default function VoiceRecorder({ seconds, level, onCancel, onSend, isSending }) {
	const [dx, setDx] = React.useState(0);
	const start = React.useRef(null);
	const history = React.useRef(Array(BARS).fill(0.1));

	// A little rolling history so the meter reads as a waveform, not one bar.
	history.current = [...history.current.slice(1), Math.max(0.1, level)];

	const onPointerDown = (e) => {
		if (e.pointerType !== 'touch') return;
		start.current = e.clientX;
	};
	const onPointerMove = (e) => {
		if (start.current === null) return;
		setDx(Math.min(0, e.clientX - start.current));
	};
	const onPointerUp = () => {
		if (start.current === null) return;
		const cancelled = dx <= -CANCEL_PX;
		start.current = null;
		setDx(0);
		if (cancelled) onCancel();
	};

	const cancelling = dx <= -CANCEL_PX;

	return (
		<Flex
			align="center"
			flex="1"
			px="3"
			h="44px"
			bg="wa.input"
			borderRadius="24px"
			userSelect="none"
			transform={`translateX(${dx}px)`}
			transition={start.current === null ? 'transform 160ms ease' : 'none'}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerCancel={onPointerUp}
			role="status"
			aria-live="polite"
			aria-label={`Recording, ${mmss(seconds)}`}
		>
			<IconButton
				aria-label="Discard recording"
				icon={<Icon as={FiTrash2} boxSize="22px" />}
				size="sm"
				variant="ghost"
				borderRadius="full"
				color={cancelling ? 'wa.recording' : 'wa.inkMuted'}
				_hover={{ color: 'wa.recording' }}
				onClick={onCancel}
			/>
			<Box
				w="10px"
				h="10px"
				borderRadius="full"
				bg="wa.recording"
				ml="2"
				mr="2"
				sx={{
					animation: `${pulse} 1.2s infinite ease-in-out`,
					'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
				}}
			/>
			<Text fontSize="sm" color="wa.ink" fontVariantNumeric="tabular-nums" minW="38px">
				{mmss(seconds)}
			</Text>
			<HStack spacing="0.5" flex="1" mx="3" h="24px" align="center" justify="center" aria-hidden="true">
				{history.current.map((v, i) => (
					<Box key={i} w="3px" borderRadius="full" bg="wa.accent" h={`${Math.max(3, Math.round(v * 22))}px`} transition="height 80ms ease" />
				))}
			</HStack>
			<HStack spacing="0" color="wa.inkMuted" display={{ base: 'flex', md: 'none' }} mr="2">
				<Icon as={FiChevronLeft} boxSize="14px" />
				<Text fontSize="xs">{cancelling ? 'Release to cancel' : 'Slide to cancel'}</Text>
			</HStack>
			<IconButton
				aria-label="Send voice message"
				icon={<Icon as={FiSend} boxSize="22px" />}
				size="sm"
				borderRadius="full"
				bg="wa.accent"
				color="wa.accentInk"
				_hover={{ bg: 'wa.accentHover' }}
				isLoading={isSending}
				onClick={onSend}
			/>
		</Flex>
	);
}
